import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { IonNav } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { CommonService } from 'src/app/services/common.service';
import { DynamicFormsService } from 'src/app/services/dynamic-forms.service';
import { applyFormVariables } from './form-variables';
import { collectVisibleComponentKeys } from './form-visibility';

@Component({
  selector: 'app-custom-form',
  templateUrl: './custom-form.page.html',
  styleUrls: ['./custom-form.page.scss'],
})
export class CustomFormPage implements OnInit {
  // The <formio> instance, read at submit time to record which conditional
  // blocks were on screen. Named differently from the template ref it queries,
  // because formObj below already holds the form record.
  @ViewChild('formObj') private formioRef: any;
  @Input() case_id: string;
  @Input() form_slug: string;
  @Input() debtor_id: string;
  @Input() dpa_check: string;
  @Input() isFromPaymentPlan: boolean = false;
  /**
   * Values for the [[token]] placeholders a form author can type into any label
   * or HTML element. Supplied by the page that opens the form (the ACH pages
   * pass the payment details the debtor just entered) - the server cannot fill
   * these in because it never sees them at render time.
   */
  @Input() variables: { [key: string]: string } = {};
  @Input() onFormSubmitted?: () => void;
  @Input() onCancel?: () => void;
  @Input() closeFn?: () => void;
  @Input() prefillData?: any;
  jsonObject: any;
  jsonString: any;
  formConfig: any;
  loader;
  formObj;

  constructor(
    private commonService: CommonService,
    private dynamicFormsService: DynamicFormsService,
    private translate: TranslateService,
    private router: Router,
    private ionNav: IonNav
  ) { }

  ngOnInit() {
    this.formConfig = {
      options: {
        alerts: {
          submitMessage: 'Thank you for submitting the form.'
        },
        errors: {
          message: 'Error while submitting the form. Please try again.'
        }
      }
    };
    this.getForm();
  }

  getForm(){
    this.commonService.showLoader(this.translate.instant('COMMON.LOADING'), 5000);
    this.dynamicFormsService.getForm(this.form_slug,this.case_id,this.debtor_id,this.dpa_check).subscribe(async(res)=>{

      this.commonService.dismissLoader();
      if(res['result']){
        if(res['data'].length > 0){
          this.setForm(res);
        }
      }else {
        this.commonService.showToast(res['message'],'danger');
      }
    });
  }

  setForm(obj){
    this.formObj = obj.data[0];
    this.jsonString = obj.data[0].content;
    this.jsonObject = JSON.parse(this.jsonString);
    if(this.jsonObject.display == 'form'){
			this.jsonObject.components.forEach( (component, index)=> {
        if(component.type == 'htmlelement'){
          var str = component.content;
          this.jsonObject.components[index].content = str.replace(/\\/g, '');
        }
      });
		} else if(this.jsonObject.display == 'wizard'){
      this.jsonObject.components.forEach( (page, index)=> {
        page.components.forEach((component,i) => {
          if(component.type == 'htmlelement'){
            var str = component.content;
            this.jsonObject.components[index].components[i].content = str.replace(/\\/g, '');
          }
        });
      });
		}

    this.applyVariables(this.jsonObject.components);

  }

  /**
   * Substitute the values supplied by the calling page into the definition, so a
   * paragraph can read the payment details back to the debtor instead of echoing
   * them as read-only fields. See form-variables.ts.
   */
  private applyVariables(components: any[]) {
    applyFormVariables(components, this.variables);
  }

  

  cancel() {
    if (this.onCancel) {
      this.onCancel();
    }
    this.close();
  }
  private close() {
    if (this.closeFn) {
      this.closeFn();
    } else {
      this.ionNav.pop();
    }
  }

  onRender(event) {
    // console.log(event);
  }
  onNext(event) {
    // console.log(event);
  }
  async onChange(event) {
    // console.log(event)
  }

  async onSubmit(event) {
    this.commonService.showLoader();
    let formHtml = '';
    if(this.form_slug.includes('sign')){
      const formContainer = document.getElementById('myFormio');
      const submitButton = formContainer.querySelector('.btn.btn-primary[type="submit"]') as HTMLElement;
      submitButton.remove();
      formHtml = formContainer.innerHTML;
    }
    
    // case_form_* entries (e.g. the ACH disclosure form) are read by the case-actions "Case Forms"
    // admin panel (edit_case_form_details, view_details, delete_form), which expects form data
    // wrapped as {formData:{data:{...}}} and relies on data.submit to detect a real submission -
    // matching what the admin's own "Fill Form"/"Edit" screens already send.
    const isCaseForm = this.form_slug.startsWith('case_form');
    const submissionData = isCaseForm
      ? { submit: true, formData: { data: event.data } }
      : event.data;
    
    // Add flag to indicate this is from payment plan if needed
    const payload = {
      data: submissionData,
      formHtml: formHtml,
      isFromPaymentPlan: this.isFromPaymentPlan,
      updateAccountStatus: this.isFromPaymentPlan && this.form_slug === 'iande', // Update status only for I&E form from payment plan
      // Sent so the server can resolve the same [[token]]s when it records the
      // disclosure wording. The wording still comes from the form definition -
      // only these values, which the debtor entered anyway, come from here.
      variables: this.variables,
      // Which components were on screen. A conditional block's condition can be
      // arbitrary JavaScript, so only this render knows which variation of the
      // wording the debtor actually agreed to - see form-visibility.ts.
      visible_components: collectVisibleComponentKeys(this.formioRef)
    };
    
    this.dynamicFormsService.saveFormData(this.form_slug, this.case_id, this.debtor_id, payload, this.dpa_check).subscribe(async (res) => {
      if (this.form_slug.startsWith('case_form_NACHA') || this.form_slug.startsWith('case_form_CC_AUTH')) {
        this.commonService.dismissLoader();
        this.jsonObject = {};
        if (res['result']) {
          if (this.onFormSubmitted) {
            this.onFormSubmitted();
          }
          this.close();
        } else {
          this.commonService.showToast('Failed to Submit!', 'danger');
        }
        return;
      }
      if(res['result']){
        this.jsonObject = {};
        // Show different success message for payment plan I&E submission
        if (this.isFromPaymentPlan && this.form_slug === 'iande') {
          this.commonService.showToast("Thanks. We'll review this and be in touch shortly. Your account has now been placed on hold.", 'success');
        } else {
          this.commonService.showToast(this.translate.instant('PAYMENT.PAYMENT_PLAN_OPTION.FORM.SUCCESS_MESSAGE'), 'success');
        }
      } else {
        this.jsonObject = {};
        this.commonService.showToast('Failed to Submit!','danger');
      }
      this.router.navigate(['/main']);
      this.commonService.dismissLoader();
    });
  }

}
